import React from 'react';
import { Box, Card, CardActionArea, CardContent, Chip, Stack, Typography } from '@mui/material';


import type { TrendArticle } from '../types';
import {
  getRunPresentationColor,
  getRunPresentationLabel,
  isLatestRunPresentation,
} from '../runPresentationUtils';
import {
  ARTICLE_CARD_TITLE_SX,
  CARD_HOVER_SX,
  CARD_INGRESS_SX,
  CARD_RADIUS,
  TREND_COLORS,
  getFallbackArticleImage,
  getArticleImage,
} from '../trendTheme';

type TrendArticleCardProps = {
  article: TrendArticle;
  onEdit?: (article: TrendArticle) => void;
  onReadMore?: (article: TrendArticle) => void;
};

export default function TrendArticleCard({ article, onReadMore }: TrendArticleCardProps) {
  const imageUrl = getArticleImage(article.heroImage, article.category);
  const fallbackImageUrl = getFallbackArticleImage(article.category);
  const articleIntro = article.articleSections?.articleIntro || article.excerpt || article.whyItMatters || '';

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: CARD_RADIUS,
        overflow: 'hidden',
        border: 'none',
        boxShadow: '0 2px 12px rgba(7,47,83,0.06)',
        ...CARD_HOVER_SX,
      }}
    >
      <CardActionArea
        onClick={() => onReadMore?.(article)}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <Box sx={{ position: 'relative', pt: '56.25%', overflow: 'hidden' }}>
          <Box
            component="img"
            src={imageUrl}
            alt=""
            onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
              if (event.currentTarget.src !== fallbackImageUrl) {
                event.currentTarget.src = fallbackImageUrl;
              }
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', bottom: 12, left: 12 }}>
            <Chip
              label={(article.category || 'TREND').toUpperCase()}
              size="small"
              sx={{
                bgcolor: TREND_COLORS.navy,
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.65rem',
                letterSpacing: '0.1em',
                borderRadius: 1,
              }}
            />
            {isLatestRunPresentation(article.runPresentation) && (
              <Chip
                label={getRunPresentationLabel(article.runPresentation)}
                size="small"
                color={getRunPresentationColor(article.runPresentation)}
                sx={{ fontWeight: 700, fontSize: '0.65rem', borderRadius: 1 }}
              />
            )}
          </Stack>
        </Box>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
          <Typography
            variant="subtitle1"
            sx={{
              ...ARTICLE_CARD_TITLE_SX,
              mb: 1,
            }}
          >
            {article.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              ...CARD_INGRESS_SX,
              mb: 0,
              flex: 1,
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontStyle: 'italic',
            }}
          >
            {articleIntro}
          </Typography>
          <Typography variant="caption" sx={{ mt: 1.5, fontWeight: 800, color: TREND_COLORS.navy }}>
            Läs mer
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
